import React, { useEffect, useRef, useState } from 'react';
import { isClient } from '../../utils/device-utils';

interface LazyImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'placeholder'> {
  src: string;
  alt: string;
  className?: string;
  forceRendition?: number;
}

function getRenditionSrc(src: string, width: number): string {
  // Check if the src already contains the rendition query
  const match = src.match(/([?&])im=Resize,width=(\d+)/);
  if (match) {
    // Replace only the width value
    return src.replace(/(im=Resize,width=)\d+/, `$1${width}`);
  }
  return `${src}?im=Resize,width=${width}`;
}

const LazyImage: React.FC<LazyImageProps> = ({ src, alt, className, forceRendition = 0, ...rest }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const elementRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    if (isClient() && elementRef.current && !isVisible) {
      observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              observer && observer.disconnect();
            }
          });
        },
        { threshold: 0 }
      );
      observer.observe(elementRef.current);
    }
    return () => {
      observer && observer.disconnect();
    };
  }, [isVisible]);

  const imgStyle: React.CSSProperties = {
    opacity: loaded ? 1 : 0,
    transition: 'opacity 0.3s',
  };

  return (
    <img
      ref={elementRef}
      src={isVisible ? getRenditionSrc(src, forceRendition) : undefined}
      alt={alt}
      style={imgStyle}
      className={className}
      onLoad={() => setLoaded(true)}
      {...rest}
    />
  );
};

export default LazyImage;
